import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  KeyboardSensor,
  Modifier,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';

type WithId = { id: number };

const DEFAULT_MODIFIERS: Modifier[] = [restrictToVerticalAxis, restrictToParentElement];

const useSortableList = <T extends WithId>(
  serverItems: T[],
  modifiers: Modifier[] = DEFAULT_MODIFIERS
) => {
  const [orderedItems, setOrderedItems] = useState<T[]>(serverItems);

  useEffect(() => {
    setOrderedItems(serverItems);
  }, [serverItems]);

  const isOrderChanged = useMemo(() => {
    if (orderedItems.length !== serverItems.length) return false;
    return orderedItems.some((item, i) => item.id !== serverItems[i].id);
  }, [orderedItems, serverItems]);

  const revertOrder = useCallback(() => {
    setOrderedItems(serverItems);
  }, [serverItems]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setOrderedItems((prev) => {
      const oldIndex = prev.findIndex((c) => c.id === active.id);
      const newIndex = prev.findIndex((c) => c.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return {
    orderedItems,
    isOrderChanged,
    handleDragEnd,
    revertOrder,
    sensors,
    modifiers,
  };
};

export default useSortableList;
